/**
 * Building {@link ObjRef}s: object references annotated with their audiences at emission time.
 * Always build a ref from the object as it is *at the moment described* (before or after a move), so
 * the audiences match that zone (see `visibility.ts`).
 */
import type { GameState } from '../state/game-state';
import type { Audience } from '../state/ids';
import { cardRefOf, type GameObject } from '../state/objects';
import { cardAudience, idAudience } from '../visibility/visibility';
import type { ObjRef } from './types';

/** A reference to `object` with the audiences of its current zone. */
export function refOf(state: GameState, object: GameObject): ObjRef {
  return {
    $: 'obj',
    id: object.id,
    card: cardRefOf(object),
    idTo: idAudience(state, object),
    cardTo: cardAudience(state, object),
  };
}

/** A reference whose face is shown to everyone (e.g. a facedown card revealed as it moves, CR 422.4). */
export function refShownToAll(state: GameState, object: GameObject): ObjRef {
  return { ...refOf(state, object), cardTo: 'all' };
}

/** Widens a ref's audiences (both id and face) by extra viewers. */
export function widenRef(ref: ObjRef, extra: Audience): ObjRef {
  return { ...ref, idTo: union(ref.idTo, extra), cardTo: union(ref.cardTo, extra) };
}

function union(a: Audience, b: Audience): Audience {
  if (a === 'all' || b === 'all') return 'all';
  const out = a.slice();
  for (const p of b) if (!out.includes(p)) out.push(p);
  return out;
}
